import { defineStore } from "pinia";
import type { ArtType, NewArtwork } from "~~/types/artwork";
import type { LocalizedField } from "~~/types/metadata";
import type { MetadataDTO } from "~~/types/metadata";
import type { GalleryItem } from "~/data/gallery";
import { useMessage } from "./app-toast";

export interface TranslationResponse {
    titleEn: string;
    titleRu: string;
}
const createEmptyArtwork = (): NewArtwork => ({
    artType: "painting",
    title: {
        hy: "",
        en: "",
        ru: "",
    },
    description: {
        hy: "",
        en: "",
        ru: "",
    },
    genres: [],
    styles: [],
    mediums: [],
    dimensions: {
        height: 0,
        width: 0,
        depth: 0,
        unit: "cm",
    },
    creationYear: new Date().getFullYear(),
    provenance: [],
    exhibitions: [],
    currentLocation: "Private Collection",
    colorPalette: [],
    isPublicDomain: false,
    gallery: [],
    location: {
        venue: {
            title: "Անձնական արվեստանոց",
            type: "private",
        },
        countryCode: "am",
        city: {
            en: "Yerevan",
            hy: "Երևան",
            ru: "Ереван",
        },
    },
    isContemporary: true,
    status: "published",
    price: 500,
    isForSale: false,
    priceDigital: 500,
    isForSaleDigital: false,
    thumbnail: "",
});

export interface NewArtworkStoreState {
    newArtwork: NewArtwork;
    currentStep: number;
    showNewArtwork: boolean;
    totalSteps: number;
    translating: boolean;
    saving: boolean;
    deleting: boolean;
}

export const useNewArtworkStore = defineStore("NewArtworkStore", {
    state: (): NewArtworkStoreState => ({
        newArtwork: createEmptyArtwork(),
        currentStep: 0,
        totalSteps: 0,
        translating: false,
        saving: false,
        deleting: false,
        showNewArtwork: false,
    }),

    actions: {
        openNewArtwork() {
            this.showNewArtwork = true;
            document.body.style.overflow = "hidden";
        },
        closeNewArtwork() {
            document.body.style.overflow = "auto";

            this.showNewArtwork = false;
        },
        getArtworkDraft() {
            const draft = localStorage.getItem("artworkDraft_v2");
            if (draft) {
                this.newArtwork = JSON.parse(draft);
            } else {
                this.newArtwork = createEmptyArtwork();
            }
        },

        saveArtworkDraft() {
            localStorage.setItem("artworkDraft_v2", JSON.stringify(this.newArtwork));
        },
        resetForm() {
            this.newArtwork = createEmptyArtwork();
        },
        nextStep() {
            if (this.currentStep >= this.totalSteps - 1) return;
            this.currentStep = this.currentStep + 1;
        },
        prevStep() {
            if (this.currentStep < 1) return;
            this.currentStep = this.currentStep - 1;
        },
        async saveAndContinue() {
            if (this.currentStep >= this.totalSteps - 1) return;

            // Step 0: Handle Armenian Title & Description
            if (this.currentStep === 0) {
                // 1. Mandatory Title translation
                this.translateField("title");

                // 2. Optional Description translation (only if user typed something)
                if (this.newArtwork.description.hy) {
                    this.translateField("description");
                }
            }

            // Move forward immediately
            this.saveArtworkDraft();
            this.nextStep();
        },
        updateTotalSteps(steps: number) {
            this.totalSteps = steps;
        },
        setArtType(type: ArtType) {
            this.newArtwork.artType = type;
            this.saveArtworkDraft();
        },
        async resetArtType(newType: ArtType) {
            const metaStore = useMetaStore();
            this.newArtwork.artType = newType;

            this.newArtwork.genres = [] as MetadataDTO[];
            this.newArtwork.styles = [] as MetadataDTO[];
            this.newArtwork.mediums = [] as MetadataDTO[];
            await metaStore.setArtType(newType);
            this.saveArtworkDraft();
        },
        selectMedium(medium: MetadataDTO) {
            if (!this.newArtwork.mediums) {
                this.newArtwork.mediums = [];
            }
            const exists = this.newArtwork.mediums.some((m) => m.key === medium.key);

            if (exists) {
                this.newArtwork.mediums = this.newArtwork.mediums.filter(
                    (m) => m.key !== medium.key,
                );
            } else {
                this.newArtwork.mediums.unshift(medium);
            }

            this.saveArtworkDraft();
        },
        setThumbnail(url: string) {
            this.newArtwork.thumbnail = url;
            this.saveArtworkDraft();
        },
        setGalleryImage(imageUrl: string) {
            if (!this.newArtwork.gallery) {
                this.newArtwork.gallery = []
            }
            this.newArtwork.gallery.push(imageUrl)
            this.saveArtworkDraft();
        },
        setCreationYear(year: number) {
            this.newArtwork.creationYear = year;
        },
        selectGenre(genre: MetadataDTO) {
            if (!this.newArtwork || !genre) return;

            if (!this.newArtwork.genres) {
                this.newArtwork.genres = [];
            }

            const isSelected = this.newArtwork.genres.some(
                (s: MetadataDTO) => s.key === genre.key,
            );

            if (isSelected) {
                this.newArtwork.genres = this.newArtwork.genres.filter(
                    (s: MetadataDTO) => s.key !== genre.key,
                );
            } else {
                this.newArtwork.genres.push(genre);
            }
            this.saveArtworkDraft();
        },
        selectStyle(style: MetadataDTO) {
            if (!this.newArtwork || !style) return;

            if (!this.newArtwork.styles) {
                this.newArtwork.styles = [];
            }

            const isSelected = this.newArtwork.styles.some(
                (s: MetadataDTO) => s.key === style.key,
            );

            if (isSelected) {
                this.newArtwork.styles = this.newArtwork.styles.filter(
                    (s: MetadataDTO) => s.key !== style.key,
                );
            } else {
                this.newArtwork.styles.push(style);
            }
            this.saveArtworkDraft();
        },
        setStep(step: number) {
            this.currentStep = step;
        },
        setCountry(code: string) {
            if (!this.newArtwork.location) {
                this.newArtwork.location = {
                    venue: {
                        title: "Անձնական արվեստանոց",
                        type: "private",
                    },
                    countryCode: "am",
                    city: {
                        en: "Yerevan",
                        hy: "Երևան",
                        ru: "Ереван",
                    },
                };
            }
            this.newArtwork.location.countryCode = code;
        },
        setRegion(r: LocalizedField) {
            if (!this.newArtwork.location) {
                this.newArtwork.location = {
                    venue: {
                        title: "Անձնական արվեստանոց",
                        type: "private",
                    },
                    countryCode: "am",
                    city: {
                        en: "Yerevan",
                        hy: "Երևան",
                        ru: "Ереван",
                    },
                };
            }
            this.newArtwork.location.city = r;
        },
        setVenue(v: GalleryItem) {
            if (!this.newArtwork.location) {
                this.newArtwork.location = {
                    venue: {
                        title: "Անձնական արվեստանոց",
                        type: "private",
                    },
                    countryCode: "am",
                    city: {
                        en: "Yerevan",
                        hy: "Երևան",
                        ru: "Ереван",
                    },
                };
            }
            this.newArtwork.location.venue = v;
        },
        togglePrice() {
            this.newArtwork.isForSale = !this.newArtwork.isForSale;
        },
        togglePriceDigital() {
            this.newArtwork.isForSaleDigital = !this.newArtwork.isForSaleDigital;
        },
        async translateField(field: "title" | "description") {
            const textToTranslate = this.newArtwork[field].hy;
            const { $api } = useNuxtApp()
            if (!textToTranslate) return;

            // SMART CHECK:
            // If translations exist AND the Armenian text hasn't changed
            // (We check this by seeing if a translation already exists to avoid redundant calls)
            // Note: For a truly "perfect" check, you'd need the 'lastTranslated' string,
            // but this is a solid middle ground for now.
            if (this.newArtwork[field].en && this.newArtwork[field].ru) {
                // If you want to force re-translate on change, you'd compare values here.
                // For now, this skips if data is present.
                return;
            }

            this.translating = true;

            try {
                const data = await $api<any>("/art/translate", {
                    method: 'POST',
                    body: {
                        text: textToTranslate,
                        sourceLang: "hy",
                        targetLangs: ["en", "ru"],
                    }
                });

                if (data?.success && data.data) {
                    this.newArtwork[field].en = data.data.en;
                    this.newArtwork[field].ru = data.data.ru;
                    this.saveArtworkDraft();
                }
            } catch (_e) {
            } finally {
                // Note: If both title and description are translating,
                // this flag might flicker. That's fine for background work.
                this.translating = false;
            }
        },
        async createArtwork() {
            const toast = useMessage()
            const localePath = useLocalePath()
            const { $i18n, $api } = useNuxtApp()
            // 1. Basic validation check before sending
            if (!this.newArtwork.thumbnail) {
                toast.add($i18n.t("messages.image_required_upload"),
                    "error")
                return;
            }

            this.saving = true; // Using existing loading flag or a new 'saving' one

            try {
                // 2. The POST request
                // Note: We don't send _id or userId (the backend should handle userId from the JWT)
                const body = this.newArtwork
                const data = await $api<any>("/art/create", {
                    method: 'POST',
                    body
                });
                if (data && data.success) {
                    toast.add(
                        $i18n.t("messages.artwork_publish_success"),
                        "success",
                    );

                    // 3. Cleanup: Clear the draft since it's now live on the server
                    this.resetForm();
                    localStorage.removeItem("artworkDraft_v2");
                    this.closeNewArtwork();

                    await navigateTo(localePath('/dashboard/my-artworks'))
                    this.currentStep = 0;
                    return data.artwork;
                }
            } catch (e: any) {
                toast.error(e)
                return null;
            } finally {
                this.saving = false;
            }
        },

        async deleteImage(url: string) {
            const { $api } = useNuxtApp()

            if (!url) return
            this.deleting = true
            try {
                await $api<any>(`/delete-assets`, { method: 'POST', body: { fileUrls: [url] } });
            } catch (_e) { }
            finally { this.deleting = false }
        },
        removeToReplaceImage(url: string) {
            this.newArtwork.gallery = this.newArtwork.gallery.filter(u => u !== url)
        },
        async unsetImage(url: string) {
            if (!url) return
            await this.deleteImage(url)
            this.newArtwork.gallery = this.newArtwork.gallery.filter(u => u !== url)
            this.saveArtworkDraft()
        }
    },
});
